interface EnvConfig {
  apiBaseUrl: string
  authRealm: string
  region: string
  featureFlags: string[]
  keycloakUrl: string
  keycloakClientId: string
  /** Development gate: when true (only in Vite DEV), skip Keycloak and act as authenticated. Ignored in production. */
  developmentGateBypassAuth: boolean
  /** Origins allowed to frame the embed routes and exchange postMessages (KPS-346). */
  embedAllowedParentOrigins: string[]
  /** True under a Vite dev build; gates dev-only conveniences. */
  isDevelopment: boolean
}

declare global {
  interface Window {
    /** Runtime config from /config.js (entrypoint today; CDN or API later). Overrides build-time env for listed keys. */
    __ENV?: Record<string, string>
  }
}

const parseFeatureFlags = (flags?: string): string[] => {
  if (!flags) return []
  return flags
    .split(',')
    .map(flag => flag.trim())
    .filter(Boolean)
}

import { resolveApiBaseUrl } from './api-base-url'

/**
 * Prefer runtime (window.__ENV) over build-time (import.meta.env). Runtime
 * values come from /config.js (script tag in index.html, evaluated before this
 * bundle). The deploy workflow writes config.js with the per-env apiBaseUrl,
 * keycloakUrl, authRealm (+ optional region, keycloakClientId, featureFlags).
 * When config.js is absent or empty (e.g. local dev), env.ts falls through to
 * import.meta.env.VITE_* baked at build time.
 */
const getEnv = (name: string): string | undefined => {
  const runtime = typeof window !== 'undefined' && window.__ENV?.[name]
  if (runtime != null && String(runtime).trim() !== '')
    return String(runtime).trim()
  const v = import.meta.env[name]
  return v != null ? String(v).trim() : undefined
}

const isDev = !!import.meta.env.DEV

// Runtime-aware: prefer window.__ENV (populated by /config.js before bundle executes),
// fall back to import.meta.env.VITE_API_BASE_URL (legacy nginx-in-ECS path).
const rawApiBaseUrl = getEnv('VITE_API_BASE_URL')

if (!isDev && (rawApiBaseUrl == null || rawApiBaseUrl === '')) {
  throw new Error(
    'VITE_API_BASE_URL is required for production. Set it in /config.json (S3+CloudFront path) or .env (legacy build).',
  )
}

const resolvedApiBaseUrl = resolveApiBaseUrl(isDev, rawApiBaseUrl ?? '')

const requiredEnv = (name: string): string => {
  const value = getEnv(name)
  if (!isDev && (value == null || value === '')) {
    throw new Error(
      `${name} is required for production. Set it in ECS task environment or .env (dev).`,
    )
  }
  return value ?? ''
}

const parseDevelopmentGateBypassAuth = (): boolean => {
  if (!isDev) return false
  const v = getEnv('VITE_DEVELOPMENT_GATE_BYPASS_AUTH')
  return v === 'true' || v === '1' || v === 'yes'
}

export const env: EnvConfig = {
  apiBaseUrl: resolvedApiBaseUrl,
  authRealm: requiredEnv('VITE_AUTH_REALM'),
  region: import.meta.env.VITE_REGION || 'us-east-1',
  featureFlags: parseFeatureFlags(getEnv('VITE_FEATURE_FLAGS')),
  keycloakUrl: requiredEnv('VITE_KEYCLOAK_URL'), // runtime from /config.js when deployed
  keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'gim-app',
  developmentGateBypassAuth: parseDevelopmentGateBypassAuth(),
  embedAllowedParentOrigins: parseFeatureFlags(
    getEnv('VITE_EMBED_ALLOWED_PARENT_ORIGINS'),
  ),
  isDevelopment: isDev,
}

export const isFeatureEnabled = (flag: string): boolean => {
  return env.featureFlags.includes(flag)
}
